/**
 * Map bounds and terrain features.
 *
 * Kate started with an endless map and reversed herself once Howard pointed out that
 * 7 players would never find each other on one: "Actually, the map is only small.
 * There's borders." So the map is small and hard-bounded — you are stopped at the edge,
 * not damaged, not wrapped, not teleported.
 */

/**
 * Half-width of the playable square, in metres. 90m across is small enough that seven
 * players cross paths without needing the colored map dots to hunt, which is the
 * problem the border was introduced to solve.
 */
/**
 * Kate after playing: "can you expand the world a little... the jungle map bigger."
 * Doubled from 45. Still bordered — she was firm about that on 08-16 — just bigger.
 */
export const MAP_HALF_EXTENT = 90

/** Physical radius of a player, used for collision against trees and the border. */
export const PLAYER_RADIUS = 0.4

export interface Circle {
  x: number
  z: number
  radius: number
}

/** The pond. Kate: you drink in this game, and "if you jump in water, you can wash yourself off." */
export const WATER: Circle = { x: -14, z: 12, radius: 9 }

/**
 * True when the whole map is water — the ocean biome. Kate listed ocean as one of the
 * maps the group can vote onto, with its own animals (dolphins, whales, fish).
 */
export const waterState = { flooded: false }

/** Resize the pond for the current biome, or flood the entire map. */
export function setWater(radius: number | 'flooded'): void {
  if (radius === 'flooded') {
    waterState.flooded = true
  } else {
    waterState.flooded = false
    WATER.radius = radius
  }
}

export function isInWater(x: number, z: number): boolean {
  if (waterState.flooded) return true
  return Math.hypot(x - WATER.x, z - WATER.z) < WATER.radius
}

/** True if the point lies outside the playable square. */
export function isOutsideBorder(x: number, z: number): boolean {
  const limit = MAP_HALF_EXTENT - PLAYER_RADIUS
  return Math.abs(x) > limit || Math.abs(z) > limit
}

/** Clamp a position back inside the border. Stops the player; never damages them. */
export function clampToBorder(position: { x: number; z: number }): boolean {
  const limit = MAP_HALF_EXTENT - PLAYER_RADIUS
  let clamped = false
  if (position.x > limit) { position.x = limit; clamped = true }
  else if (position.x < -limit) { position.x = -limit; clamped = true }
  if (position.z > limit) { position.z = limit; clamped = true }
  else if (position.z < -limit) { position.z = -limit; clamped = true }
  return clamped
}
